<script setup lang="ts" generic="T extends Record<string, any>">
import { computed } from 'vue'

export interface ChipItem {
  [key: string]: any
}

// TODO: Add blue them in when needed and blues have been updated to match new marketing colors

const props = withDefaults(defineProps<{
  items: T[]
  modelValue: T[]
  itemKey?: string
  itemLabel?: string
  showIcon?: boolean
  gbTheme?: 'purple'
}>(), {
  itemKey: 'value',
  itemLabel: 'label',
  showIcon: true,
  gbTheme: 'purple'
})

const emit = defineEmits<{
  'update:modelValue': [value: T[]]
}>()

const isSelected = (item: T): boolean => {
  return props.modelValue.some(selected => selected[props.itemKey] === item[props.itemKey])
}

const toggleSelection = (item: T) => {
  let newSelection: T[]
  
  if (isSelected(item)) {
    newSelection = props.modelValue.filter(selected => selected[props.itemKey] !== item[props.itemKey])
  } else {
    newSelection = [...props.modelValue, item]
  }
  
  emit('update:modelValue', newSelection)
}

const getChipClasses = computed(() => (item: T) => {
  let baseClasses = 'inline-flex items-center gap-2 rounded-lg border pr-4 py-2 text-base font-normal cursor-pointer transition-all select-none'
  
  if (isSelected(item)) {
    baseClasses += ' chip-selected pl-2 '
    if (props.gbTheme === 'purple') {
      return `${baseClasses} bg-purple-100 border-purple-300 text-gray-900 hover:bg-purple-200`
    }
  } else {
    return `${baseClasses} pl-4 bg-gray-50 border-gray-600 text-gray-900 hover:bg-gray-300`
  }
})

const getIconColor = () => {
  return props.gbTheme === 'purple' ? 'text-purple-300' : ''
}
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <label
      v-for="item in items"
      :key="item[itemKey]"
      :for="`chip-${item[itemKey]}`"
      :class="getChipClasses(item)"
    >
      <input
        :id="`chip-${item[itemKey]}`"
        type="checkbox"
        class="sr-only"
        :checked="isSelected(item)"
        :value="item[itemKey]"
        :aria-label="item[itemLabel]"
        @change="toggleSelection(item)"
      />
      <span
        v-if="showIcon && isSelected(item)"
        class="material-icons-round text-base"
        :class="getIconColor()"
        aria-hidden="true"
      >
        check
      </span>
      <span>{{ item[itemLabel] }}</span>
    </label>
  </div>
</template>

<style scoped>
label:has(:focus-visible) {
  @apply outline outline-2 outline-offset-2;
}

/* Purple theme focus - selected */
label.chip-selected:has(:focus-visible) {
  @apply outline-purple-500;
  @apply bg-purple-200;
}

/* Default (unselected) focus */
label:not(.chip-selected):has(:focus-visible) {
  @apply outline-gray-600;
  @apply bg-gray-300;
}
</style>
